import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CurrentUser, LoginRequest, LoginResponse, RoleCode } from '../models';

const STORAGE_KEY = 'istyle_session';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly currentUserSubject = new BehaviorSubject<CurrentUser | null>(this.restoreSession());
  readonly currentUser$ = this.currentUserSubject.asObservable();

  constructor(private readonly http: HttpClient) {}

  get currentUser(): CurrentUser | null {
    return this.currentUserSubject.value;
  }

  get isLoggedIn(): boolean {
    return !!this.currentUser && !!this.token;
  }

  get token(): string | null {
    return sessionStorage.getItem(`${STORAGE_KEY}_token`);
  }

  login(request: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiBaseUrl}/Auth/Login`, request).pipe(
      tap((response) => this.persistSession(response))
    );
  }

  logout(): void {
    sessionStorage.removeItem(`${STORAGE_KEY}_token`);
    sessionStorage.removeItem(`${STORAGE_KEY}_user`);
    this.currentUserSubject.next(null);
  }

  hasAnyRole(roles: RoleCode[]): boolean {
    const userRoles = this.currentUser?.roles ?? [];
    return roles.some((role) => userRoles.includes(role));
  }

  private persistSession(response: LoginResponse): void {
    const user: CurrentUser = {
      ntUserId: response.ntUserId,
      fullName: response.fullName,
      email: response.email,
      roles: response.roles
    };
    sessionStorage.setItem(`${STORAGE_KEY}_token`, response.token);
    sessionStorage.setItem(`${STORAGE_KEY}_user`, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  private restoreSession(): CurrentUser | null {
    const raw = sessionStorage.getItem(`${STORAGE_KEY}_user`);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as CurrentUser;
    } catch {
      return null;
    }
  }
}
